import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  // Person,
  Email,
  Search,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/system.store';
import { AgentService, SystemUser, UpdateSystemUserRequest } from '../../services/agent.service';

interface EditSystemUserData {
  email: string;
  fullName: string;
  isClient: boolean;
}

const EditSystemUserPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchEmail, setSearchEmail] = useState('');
  const [user, setUser] = useState<SystemUser | null>(null);
  const [formData, setFormData] = useState<EditSystemUserData>({
    email: '',
    fullName: '',
    isClient: false,
  });
  
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [errors, setErrors] = useState<Partial<EditSystemUserData>>({});
  const [success, setSuccess] = useState(false);

  const handleSearchUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchEmail.trim()) {
      setSearchError('Email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchEmail)) {
      setSearchError('Please enter a valid email address');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setUser(null);
    setSuccess(false);

    try {
      const response = await AgentService.getSystemUserByEmail(searchEmail);

      if (response.success && response.user) {
        setUser(response.user);
        setFormData({
          email: response.user.email,
          fullName: response.user.fullName,
          isClient: response.user.isClient,
        });
        dispatch(
          showSnackbar({
            isError: false,
            msg: 'User found successfully!',
          })
        );
      } else {
        setSearchError('User not found with this email address');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchError('Failed to search user. Please try again.');
      dispatch(
        showSnackbar({
          isError: true,
          msg: 'Failed to search user. Please try again.',
        })
      );
    } finally {
      setIsSearching(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<EditSystemUserData> = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !user) {
      return;
    }

    setIsUpdating(true);
    setSuccess(false);

    try {
      const updates: UpdateSystemUserRequest = {};
      
      if (formData.email !== user.email) {
        updates.email = formData.email;
      }
      
      if (formData.fullName !== user.fullName) {
        updates.fullName = formData.fullName;
      }
      
      if (formData.isClient !== user.isClient) {
        updates.isClient = formData.isClient;
      }

      if (Object.keys(updates).length === 0) {
        dispatch(
          showSnackbar({
            isError: false,
            msg: 'No changes detected',
          })
        );
        setIsUpdating(false);
        return;
      }

      const response = await AgentService.updateSystemUser({ updates, crmProfileId: user.crmProfileId , userId: user.id });

      if (response.success && response.user) {
        setSuccess(true);
        setUser(response.user);
        setFormData({
          email: response.user.email,
          fullName: response.user.fullName,
          isClient: response.user.isClient,
        });
        
        // Update search email if email was changed
        if (updates.email) {
          setSearchEmail(response.user.email);
        }
        
        dispatch(
          showSnackbar({
            isError: false,
            msg: response.message || 'User updated successfully!',
          })
        );
      } else {
        throw new Error(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: error instanceof Error ? error.message : 'Failed to update user. Please try again.',
        })
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (field: keyof EditSystemUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isClient' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
    
    // Reset success state when form changes
    if (success) {
      setSuccess(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchEmail(event.target.value);
    if (searchError) {
      setSearchError('');
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box 
        sx={{ 
          padding: { xs: "16px", md: "24px" }, 
          backgroundColor: theme.palette.grey[50], 
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton 
            onClick={() => navigate('/admin')} 
            sx={{ 
              p: 1,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: theme.palette.grey[100] }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
            Edit System User
          </Typography>
        </Box>

        {/* Search Section */}
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 600, mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
              Search User by Email
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSearchUser}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="User Email Address"
                  type="email"
                  value={searchEmail}
                  onChange={handleEmailChange}
                  error={!!searchError}
                  helperText={searchError || 'Enter the email address of the user you want to edit'}
                  required
                  InputProps={{
                    startAdornment: (
                      <Email color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  color="info"
                  disabled={isSearching || !searchEmail}
                  startIcon={isSearching ? <CircularProgress size={16} /> : <Search />}
                  sx={{ textTransform: 'none' }}
                >
                  {isSearching ? 'Searching...' : 'Search User'}
                </Button>
              </Stack>
            </form>
          </Box>
        </Paper>

        {/* Edit Section */}
        {user && (
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 600 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                Edit User Information
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {/* User Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.grey[100], borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Current User:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {user.email}
                  </Typography>
                  <Chip 
                    label={user.isClient ? 'Client User' : 'System User'} 
                    color={user.isClient ? 'primary' : 'secondary'} 
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>

              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  User updated successfully!
                </Alert>
              )}

              <form onSubmit={handleUpdateUser}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    error={!!errors.email}
                    helperText={errors.email || 'Changing the email will update the user\'s login credentials'}
                    required
                    InputProps={{
                      startAdornment: (
                        <Email color="action" sx={{ mr: 1 }} />
                      ),
                    }}
                  />
                  
                  {/* <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange('fullName')}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    required
                    InputProps={{
                      startAdornment: (
                        <Person color="action" sx={{ mr: 1 }} />
                      ),
                    }}
                  /> */}

                  {/* <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isClient}
                        onChange={handleInputChange('isClient')}
                        color="primary"
                      />
                    }
                    label="Is Client User"
                  /> */}

                  {/* <Alert severity="info">
                    <strong>Note:</strong> Changing the email will update the user's login credentials. 
                    If "Is Client User" is enabled, the user will have access to client-specific features.
                  </Alert> */}

                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={isUpdating || !formData.email || !formData.fullName}
                    startIcon={isUpdating ? <CircularProgress size={16} /> : <Edit />}
                    sx={{ textTransform: 'none' }}
                  >
                    {isUpdating ? 'Updating User...' : 'Update User'}
                  </Button>
                </Stack>
              </form>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default EditSystemUserPage;